import React, { useRef, useCallback, memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    EditingState,
    TreeDataState,
    CustomTreeData,
    SummaryState,
    IntegratedSummary,
    GroupingState,
    IntegratedGrouping,
} from '@devexpress/dx-react-grid';
import {
    DataTypeProvider,
    FilteringState,
    IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
    SelectionState,
    IntegratedSelection,
    SortingState,
    IntegratedSorting,
    PagingState,
    IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
    Plugin,
    Template,
    TemplatePlaceholder,
} from '@devexpress/dx-react-core';
import {
    Grid,
    VirtualTable,
    TableHeaderRow,
    TableSelection,
    TableTreeColumn,
    TableColumnVisibility,
    ColumnChooser,
    TableSummaryRow,
    TableFilterRow,
    TableBandHeader,
    ExportPanel,
    Toolbar,
    DragDropProvider,
    TableGroupRow,
    GroupingPanel,
    PagingPanel,
    Table,
    TableEditRow,
    TableEditColumn,
    TableColumnReordering,
    TableFixedColumns
} from '@devexpress/dx-react-grid-material-ui';
import _ from 'lodash';
import saveAs from 'file-saver';
import { IconButton, Tooltip } from '@material-ui/core';
import { PictureAsPdf, FilterList, Autorenew } from '@material-ui/icons';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { onError } from '../../components/shared/messages';
import { useLoader } from '../../components/shared/utils';
import { PopupEditing } from './popupEditing'
import './style.css';
import './arial-normal.js';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { Icon } from 'semantic-ui-react';


const GridPanel = ({ options, data, onUpdate, onSelected }) => {
    const exporterRef = useRef();
    const loader = useLoader();
    const [selection, setSelection] = useState([]);
    const [showFilter, setShowFilter] = useState(options.showFilter);

    if(options.customOptions){
        options.initialCustomOptions = JSON.parse(JSON.stringify(options.customOptions));
    }

    if (options.customOptions?.order && !options.customOptions?.order?.ordering) {
        options.customOptions.order.ordering = options.columns.map(x => x.name);
    }
    if (options.customOptions?.hidden && !options.customOptions?.hidden?.hiddenColumns) {
        options.customOptions.hidden.hiddenColumns = [];
    }

    const [customOptions, setCustomOptions] = options.cacheId ? useLocalStorageState(
        `${options.cacheId}-GridOptions-${window.user?.UserName}`,
        options.customOptions) : useState(options.customOptions);

    useEffect(() => {
        if (options?.customOptions?.overrideData) {
            // use customOptions.overrideData in case the columns or other properties are being changed from the nesting component
            setCustomOptions(options.customOptions);
        }
    }, [options.customOptions]);

    useEffect(() => {
        setSelection([])
        onSelected?.([])
    }, [data])

    const excelHiddenColumnNames = useMemo(
        () => (options.excel?.hiddenColumns || []).map((c) => c.columnName),
        [options.excel?.hiddenColumns]
    );


    const groupingChange = (groups) => {
        const newCustomOptions = { ...customOptions };
        newCustomOptions.group.grouping = groups;
        setCustomOptions(newCustomOptions);
    }

    const orderingChange = (order) => {
        const newCustomOptions = { ...customOptions };
        newCustomOptions.order.ordering = order;
        setCustomOptions(newCustomOptions);
    }

    const hiddenColumnsChange = (columns) => {
        let newCustomOptions = { ...customOptions };
        newCustomOptions.hidden.hiddenColumns = columns.map((x) => ({
            columnName: x,
            togglingEnabled: newCustomOptions.hidden.hiddenColumns.find(c => c.columnName == x)?.togglingEnabled
        }))
        setCustomOptions(newCustomOptions);
    }

    const commitChanges = ({ added, changed, deleted }) => {
        if (onUpdate) {
            onUpdate(added, changed, deleted);
        }
    };

    const onSelectionChange = (selection) => {
        setSelection(selection)
        onSelected?.(selection)
    }

    const CustomCell = memo((props) => {
        const cell = <VirtualTable.Cell {...props}></VirtualTable.Cell>;
        if (options.customCellCallback) {
            const custom = options.customCellCallback(props);
            return custom ? <VirtualTable.Cell>{custom}</VirtualTable.Cell> : cell;
        }
        return cell;
    });
    CustomCell.displayName = 'CustomCell';

    const CustomTreeCell = memo((props) => {
        const cell = <TableTreeColumn.Cell {...props}></TableTreeColumn.Cell>;
        if (options.customTreeCellCallback) {
            const custom = options.customTreeCellCallback(props);
            return custom ? (
                <TableTreeColumn.Cell>{custom}</TableTreeColumn.Cell>
            ) : (
                cell
            );
        }
        return cell;
    });
    CustomTreeCell.displayName = 'CustomTreeCell';

    const CustomSelectionCell = memo((props) => {
        const selectable = options.selectableCallback
            ? options.selectableCallback(props.row)
            : true;
        return selectable ? (
            <TableSelection.Cell {...props} />
        ) : (
            <VirtualTable.Cell {...props}>
                {options.noSelectionCellContent}
            </VirtualTable.Cell>
        );
    });
    CustomSelectionCell.displayName = 'CustomSelectionCell';

    const CustomRow = memo((props) => {
        return <VirtualTable.Row {...props} height={50}></VirtualTable.Row>;
    });
    CustomRow.displayName = 'CustomRow';

    const CustomEditor = (props) => {
        // eslint-disable-next-line react/prop-types
        let { value, column, onValueChange, ...rest } = props;
        if (column.valueField) {
            if (_.isArray(value)) {
                value = _.map(value, (v) => v[column.valueField] || v);
            } else if (_.isFunction(column.valueField)) {
                value = column.valueField(value);
            } else if (value) {
                value = value[column.valueField];
            }
        }
        let customprops = {};
        if (column.customProps) {
            if (_.isFunction(column.customProps)) {
                customprops = column.customProps(props);
            } else {
                customprops = column.customProps;
            }
        }
        return React.cloneElement(column.editor, {
            value,
            onChange: (newValue, data) => {
                if (data?.value) {
                    newValue = data?.value;
                }
                onValueChange(newValue);
                if (column.editor.props.onChange) {
                    column.editor.props.onChange(newValue);
                }
            },
            ...rest,
            ...customprops,
        });
    };
    CustomEditor.displayName = 'CustomEditor';
    CustomEditor.propTypes = {
        onValueChange: PropTypes.func,
        value: PropTypes.any,
    };

    const CustomFormatter = memo((props) => {
        const { column, row, value } = props;
        if (column.formatter) {
            return column.formatter(props);
        } else if (_.isArray(value)) {
            return _.join(
                _.map(value, (item) => {
                    if (_.isObject(item)) {
                        if (item[column.displayField]) {
                            return item[column.displayField];
                        } else {
                            //"Could not find:" + column.displayField
                            ('Please select');
                        }
                    } else {
                        return item;
                    }
                }),
                ','
            );
        } else if (column.displayField) {
            if (_.isObject(value) && value[column.displayField]) {
                return value[column.displayField];
            } else if (row[column.displayField]) {
                return row[column.displayField];
            } else {
                //return "Could not find:" + column.displayField
                return 'Please select';
            }
        } else {
            return value;
        }
    });
    CustomFormatter.displayName = 'CustomFormatter';
    CustomFormatter.propTypes = {
        value: PropTypes.any,
    };

    const CustomEditorProvider = memo((props) => {
        return (
            <DataTypeProvider
                editorComponent={CustomEditor}
                formatterComponent={CustomFormatter}
                {...props}
            />
        );
    });
    CustomEditorProvider.displayName = 'CustomEditorProvider';

    const CustomValueFormatter = memo((props) => {
        const { formatter, ...rest } = props;
        return <DataTypeProvider formatterComponent={formatter} {...rest} />;
    });
    CustomValueFormatter.displayName = 'CustomValueFormatter';
    CustomValueFormatter.propTypes = {
        formatter: PropTypes.any,
    };

    const CustomToolbarMarkup = ({ children }) => (
        <Plugin name='customToolbarMarkup'>
            <Template name='toolbarContent'>
                <TemplatePlaceholder />
                {children}
            </Template>
        </Plugin>
    );
    CustomToolbarMarkup.propTypes = {
        children: PropTypes.any,
    };

    const printPdf = useCallback(async () => {
        try {
            loader(true);
            const doc = new jsPDF({
                orientation: 'landscape',
            });
            doc.setFont('arial');
            const userLang = window.optima?.user?.UserSettings[0]?.Language
            const rtl = userLang?.LocaleID === 'he-IL' || userLang?.Code === 'he' || userLang?.Code === 'he-IL' || userLang?.Id === 7 // 7 is hebrew
            doc.setR2L(rtl)

            const cols = options.columns.filter((col) => !col.excludePdf);

            const getColNameFormatted = (row, col) => {
                let value = row[col.name];
                if (col.formatter) {
                    value = col.formatter({ value, row });
                }
                if (col.getCellValue) {
                    value = col.getCellValue(row, col.name);
                }
                if (col.html) {
                    value = row[col.name]?.props?.dangerouslySetInnerHTML?.__html;
                }
                return value;
            };

            const tables = _.groupBy(data, (row) => {
                const groups = customOptions?.group?.grouping.map(x=>x.columnName)
                const columns = cols.filter(x=>groups.includes(x.name))
                const titles = columns.map(col=>`${col.title}: ${getColNameFormatted(row, col)}`)
                return titles.reverse().join(', ')
            });

            const filteredColumns = cols
                .filter((col) => !customOptions?.group?.grouping?.some((g) => g.columnName == col.name))
                .filter((col) => !customOptions?.hidden.hiddenColumns?.some((c) => c.columnName == col.name));

            const keys = Object.keys(tables);

            const transformData = (data) => {
                return data.map((row) => {
                    return filteredColumns.map((col) => getColNameFormatted(row, col));
                });
            };

            keys.forEach((key) => {
                const lastTableY = doc.lastAutoTable.finalY;
                doc.text(key, 14, lastTableY ? lastTableY + 10 : 20, {isInputRtl:rtl, isOutputRtl:rtl, isInputVisual:rtl});
                doc.autoTable({
                    styles: {font: 'arial'},
                    head: [filteredColumns.map((v) => v.title)],
                    body: transformData(tables[key]),
                    startY: lastTableY ? lastTableY + 15 : 25
                });
            });

            doc.save(`${options.title}-${moment().format('YYYY-MM-DD')}.pdf`);
            loader(false);
        } catch (error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [data, options.title, customOptions?.group?.grouping, customOptions?.hidden?.hiddenColumns]);

    const ExportPDFButton = () => (
        <div>
            <Tooltip title='PDF' placement='bottom' enterDelay={300}>
                <IconButton size='medium' onClick={printPdf}>
                    <PictureAsPdf />
                </IconButton>
            </Tooltip>
        </div>
    );
    const ExportExcelButton = ({ ...params }) => {
        return (
            <div>
                <Tooltip title='Excel' placement='bottom' enterDelay={300}>
                    <IconButton size='small' onClick={params.onToggle} {...params}>
                        <Icon name='file excel'></Icon>
                    </IconButton>
                </Tooltip>
            </div>
        );
    };

    const ShowFilterButton = () => (
        <div>
            <Tooltip title='Show Filter' placement='bottom' enterDelay={300}>
                <IconButton size='medium' onClick={() => setShowFilter(!showFilter)}>
                    <FilterList />
                </IconButton>
            </Tooltip>
        </div>
    );

    const ResetCustomOptionsButton = () => (
        <div>
            <Tooltip title='Reset' placement='bottom' enterDelay={300}>
                <IconButton size='medium' onClick={() => { setCustomOptions(options.initialCustomOptions) }}>
                    <Autorenew />
                </IconButton>
            </Tooltip>
        </div>
    );

    const onSave = (workbook) => {
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
                new Blob([buffer], { type: 'application/octet-stream' }),
                `${options.title}.xlsx`
            );
        });
    };

    const startExport = useCallback(() => {
        exporterRef.current.exportGrid();
    }, [exporterRef]);

    const Root = (props) => <Grid.Root {...props} style={{ flex: 1, overflow: 'hidden' }} />;

    const toolbar =
        options.export || customOptions?.group || customOptions?.hidden || options.menu || options.pdf;

    const groupRow = options.leftColumns || customOptions?.group;

    return (
        <React.Fragment>
            {data && (
                <Grid
                    rootComponent={Root}
                    rows={data}
                    columns={options.columns}
                    getRowId={options.getRowId}
                >
                    {options.columns
                        .filter((column) => column.formatter)
                        .map((column) => {
                            return (
                                <CustomValueFormatter
                                    key={column.name}
                                    for={[column.name]}
                                    formatter={column.formatter}
                                />
                            );
                        })}

                    {options.columns
                        .filter((column) => column.editor)
                        .map((column) => {
                            return (
                                <CustomEditorProvider
                                    key={column.name}
                                    for={[column.name]}
                                    editor={column.editor}
                                />
                            );
                        })}

                    {options.tree && (
                        <TreeDataState
                            defaultExpandedRowIds={options.tree.defaultExpandedRows}
                        />
                    )}
                    {options.tree && (
                        <CustomTreeData getChildRows={options.tree.getChildRows} />
                    )}

                    {customOptions?.sort && (
                        <SortingState defaultSorting={customOptions?.sort.sorting} />
                    )}
                    {customOptions?.sort && <IntegratedSorting />}

                    {customOptions?.group && <DragDropProvider />}
                    {customOptions?.group && (
                        <GroupingState
                            grouping={customOptions?.group?.grouping || []}
                            expandedGroups={customOptions?.group?.expandedGroups}
                            defaultGrouping={customOptions?.group?.grouping || []}
                            defaultExpandedGroups={customOptions?.group?.defaultExpandedGroups || []}
                            columnExtensions={customOptions?.group.extensions || []}
                            onGroupingChange={groupingChange}
                        />
                    )}
                    {customOptions?.group && <IntegratedGrouping />}

                    {options.summary && (
                        <SummaryState
                            totalItems={options.totalSummaryItems}
                            groupItems={options.groupSummaryItems}
                        />
                    )}
                    {options.summary && <IntegratedSummary />}

                    {options.selectable && (
                        <SelectionState
                            onSelectionChange={onSelectionChange}
                            selection={selection}
                        />
                    )}
                    {options.selectable && <IntegratedSelection />}

                    {options.paging && (
                        <PagingState defaultCurrentPage={0} pageSize={options.paging.pageSize || 50} />
                    )}
                    {options.paging && <IntegratedPaging />}

                    {options.filtering && <FilteringState defaultFilters={[]} />}
                    {options.filtering && (
                        <IntegratedFiltering
                            columnExtensions={options.filtering.extensions}
                        />
                    )}

                    {options.editable && (
                        <EditingState
                            onCommitChanges={commitChanges}
                            columnExtensions={options.columnExtensions}
                            {...options.editable.extraProps}
                        />
                    )}

                    {options.noVirtualTable ? (
                        <Table
                            cellComponent={CustomCell}
                            rowComponent={CustomRow}
                            columnExtensions={options.columnExtensions}
                        />
                    ) : (
                        <VirtualTable
                            cellComponent={CustomCell}
                            rowComponent={CustomRow}
                            columnExtensions={options.columnExtensions}
                            height='auto'
                        />
                    )}
                    <TableHeaderRow showSortingControls={customOptions?.sort} />
                    <TableColumnVisibility
                        defaultHiddenColumnNames={customOptions?.hidden?.hiddenColumns?.map(x => x.columnName)}
                        hiddenColumnNames={customOptions?.hidden?.hiddenColumns?.map(x => x.columnName)}
                        columnExtensions={customOptions?.hidden?.columnsExtensions}
                        onHiddenColumnNamesChange={hiddenColumnsChange}
                    />
                    {options.selectable && (
                        <TableSelection
                            showSelectAll
                            showSelectionColumn
                            cellComponent={CustomSelectionCell}
                        />
                    )}
                    {options.columnBands && (
                        <TableBandHeader columnBands={options.columnBands} />
                    )}
                    {options.tree && (
                        <TableTreeColumn
                            for={options.tree.treeColumnName}
                            cellComponent={CustomTreeCell}
                        />
                    )}
                    {options.summary && <TableSummaryRow />}
                    {options.editable && <TableEditRow />}
                    {options.editable && (
                        <TableEditColumn
                            showAddCommand={options.editable?.allowAdd}
                            showEditCommand={options.editable?.allowEdit ?? true}
                            showDeleteCommand={options.editable?.allowDelete}
                        />
                    )}
                    {options.editable?.modalEditor && (
                        <PopupEditing
                            popupComponent={options.editable.modalEditor.modal}
                            extendedProps={options.editable.modalEditor.props}
                        />
                    )}

                    {customOptions?.order && (
                        <TableColumnReordering
                            order={customOptions?.order.ordering || []}
                            onOrderChange={orderingChange} />)}
                    {groupRow && <TableGroupRow />}
                    {options.filtering && showFilter && <TableFilterRow />}
                    {options.leftColumns && (
                        <TableFixedColumns leftColumns={options.leftColumns} />
                    )}
                    {toolbar && <Toolbar />}
                    {customOptions?.group && <GroupingPanel />}
                    {options.menu && <CustomToolbarMarkup>{options.menu}</CustomToolbarMarkup>}
                    {customOptions?.hidden?.toggleHidden && <ColumnChooser />}
                    {options.export && (
                        <ExportPanel
                            startExport={startExport}
                            toggleButtonComponent={ExportExcelButton}
                        />
                    )}
                    {options.paging && <PagingPanel />}
                    {options.pdf && (
                        <CustomToolbarMarkup>
                            <ExportPDFButton />
                        </CustomToolbarMarkup>
                    )}
                    {options.filtering && (
                        <CustomToolbarMarkup>
                            <ShowFilterButton />
                        </CustomToolbarMarkup>
                    )}

                    {options.customOptions && (
                        <CustomToolbarMarkup>
                            <ResetCustomOptionsButton />
                        </CustomToolbarMarkup>
                    )}
                </Grid>
            )}
            <GridExporter
                ref={exporterRef}
                rows={data}
                columns={options.columns.filter(c =>
                    !excelHiddenColumnNames.includes(c.name) //Filter hidden columns from Excel file
                ).map((x) => {
                    return {
                        ...x,
                        title: x.exportTitle || x.title,
                    };
                })}
                {...options.excel}
                {...{ grouping :options?.customOptions?.group?.grouping }} //Added dynamic excel grouping by user's choice
                onSave={onSave}
            ></GridExporter>
        </React.Fragment>
    );
};
GridPanel.propTypes = {
    options: PropTypes.any,
    data: PropTypes.any,
    column: PropTypes.any,
    onUpdate: PropTypes.func,
    onUncommittedRowChange: PropTypes.func,
    onSelected: PropTypes.func,
    style: PropTypes.any,
    row: PropTypes.any,
    title: PropTypes.string
};
export default GridPanel;
